import { Link } from "react-router-dom";
import { Star, GitCompareArrows, TicketPercent, GraduationCap, Bell, Instagram, Twitter, MessagesSquare, ArrowRight } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Reveal, { SectionLabel } from "../components/Reveal";

const SOCIALS = [
  { icon: Instagram, label: "Instagram", href: "https://www.instagram.com/kaitofx_ftc", id: "instagram" },
  { icon: Twitter, label: "X (Twitter)", href: "https://x.com/kaitofxftc", id: "x" },
  { icon: MessagesSquare, label: "Discord", href: "https://discord.gg/a7899ShVPH", id: "discord" },
];

const WHAT_WE_DO = [
  { icon: Star, title: "Prop Firm Reviews", text: "Detailed reviews covering trading conditions, account options, pricing, and key policies." },
  { icon: GitCompareArrows, title: "Firm Comparisons", text: "Compare different prop firms to find options that suit your trading style and budget." },
  { icon: TicketPercent, title: "Discounts & Promo Codes", text: "Discover available offers and promotional deals from selected prop firms." },
  { icon: GraduationCap, title: "Trading Education", text: "Access educational content designed to improve your understanding of trading, risk management, and prop firm challenges." },
  { icon: Bell, title: "Industry Updates", text: "Stay informed about important changes to firm rules, payout policies, and other developments that may affect traders." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <Header />
      <section className="relative overflow-hidden border-b border-line">
        <div className="scanlines-overlay" />
        <div className="absolute -left-24 top-0 h-64 w-64 rounded-full bg-[#8B5CF6]/15 blur-[130px]" />
        <div className="absolute -right-20 bottom-0 h-64 w-64 rounded-full bg-[#3B82F6]/15 blur-[130px]" />
        <div className="relative mx-auto max-w-4xl px-5 py-20 text-center lg:px-8">
          <Reveal>
            <SectionLabel>About Us</SectionLabel>
            <h1 className="mt-3 font-display text-4xl font-800 tracking-tight text-white sm:text-5xl" data-testid="about-title">
              Built for traders. <span className="text-gradient">Driven by knowledge.</span>
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-muted">
              Welcome to Funded Traders Club — a platform dedicated to helping traders make informed decisions
              when choosing proprietary trading firms.
            </p>
            <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-muted">
              Founded by <span className="font-700 text-white">Akash Kumar Rai</span>, Funded Traders Club was built with a
              clear mission: to make prop firm information more accessible, transparent, and useful for traders of
              all experience levels.
            </p>
            <div className="mt-8 flex justify-center gap-3" data-testid="about-socials">
              {SOCIALS.map((s) => (
                <a key={s.id} href={s.href} target="_blank" rel="noopener noreferrer" data-testid={`about-social-${s.id}`}
                  aria-label={s.label}
                  className="flex h-11 w-11 items-center justify-center rounded-full border border-line bg-elevate text-muted transition-all duration-200 hover:-translate-y-0.5 hover:border-signal/60 hover:text-signal">
                  <s.icon size={17} />
                </a>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-5 py-16 lg:px-8">
        <Reveal>
          <div className="card-violet p-7 sm:p-10" data-testid="about-why">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-signal">{"// Why We Exist"}</p>
            <p className="mt-4 text-base leading-relaxed text-muted">
              We understand that choosing the right prop firm can be challenging. With so many firms offering
              different account sizes, evaluation models, profit splits, trading rules, and payout policies,
              traders need reliable information before investing their hard-earned money.
            </p>
            <p className="mt-4 font-display text-xl font-700 text-white">That's where we come in.</p>
          </div>
        </Reveal>

        <Reveal>
          <div className="mt-14" data-testid="about-what-we-do">
            <SectionLabel>What We Do</SectionLabel>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted">
              We focus on providing valuable resources to help traders navigate the prop trading industry:
            </p>
            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              {WHAT_WE_DO.map((w) => (
                <div key={w.title} className="card-violet p-6" data-testid={`about-card-${w.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-signal/30 bg-signal/10">
                    <w.icon size={18} className="text-signal" />
                  </span>
                  <h3 className="mt-4 font-display text-lg font-700 text-white">{w.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted">{w.text}</p>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        <Reveal>
          <div className="card-violet mt-14 p-7 sm:p-10" data-testid="about-mission">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-signal">{"// Our Mission"}</p>
            <p className="mt-4 text-base leading-relaxed text-muted">
              Our mission is to empower traders with information, encourage responsible trading, and help them
              better understand the opportunities and risks associated with proprietary trading firms.
            </p>
            <p className="mt-4 text-base leading-relaxed text-muted">
              We believe that every trader deserves access to clear information before making a decision. Our goal
              is to build a community where knowledge, transparency, and informed decision-making come first.
            </p>
          </div>
        </Reveal>

        <Reveal>
          <div className="card-violet mt-6 p-7 sm:p-10" data-testid="about-commitment">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-signal">{"// Our Commitment To Traders"}</p>
            <p className="mt-4 text-base leading-relaxed text-muted">
              At Funded Traders Club, we aim to prioritize the interests of traders by presenting useful
              information, highlighting important terms and conditions, and encouraging readers to conduct their
              own research before purchasing any prop firm challenge.
            </p>
            <p className="mt-4 text-base leading-relaxed text-muted">
              While we may feature affiliate links or promotional partnerships, our goal is to help traders make
              informed choices. Readers should always verify the latest rules, fees, restrictions, and payout terms
              directly with the relevant firm.
            </p>
          </div>
        </Reveal>

        <Reveal>
          <div className="mt-14 rounded-3xl border border-signal/30 bg-gradient-to-br from-signal/10 via-surface to-ink p-8 text-center sm:p-12" data-testid="about-join">
            <h2 className="font-display text-2xl font-800 tracking-tight text-white sm:text-3xl">Join the Club</h2>
            <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-muted sm:text-base">
              Whether you are a beginner exploring your first prop firm challenge or an experienced trader looking
              for new opportunities, Funded Traders Club is here to support your journey with information and
              resources that help you make smarter decisions.
            </p>
            <div className="mt-7 flex flex-wrap justify-center gap-3">
              <Link to="/firms" data-testid="about-join-firms"
                className="btn-beam btn-gradient inline-flex items-center gap-2 px-6 py-3.5 font-mono text-[11px] font-700 uppercase tracking-[0.15em] text-white">
                Browse Firms <ArrowRight size={13} />
              </Link>
              <a href="https://discord.gg/a7899ShVPH" target="_blank" rel="noopener noreferrer" data-testid="about-join-discord"
                className="inline-flex items-center gap-2 rounded-full border border-line bg-ink px-6 py-3.5 font-mono text-[11px] font-700 uppercase tracking-[0.15em] text-muted transition-colors hover:border-signal/60 hover:text-signal">
                <MessagesSquare size={13} /> Join Discord
              </a>
            </div>
            <p className="mt-8 font-display text-sm font-700 uppercase tracking-[0.2em] text-white">
              Funded Traders Club — <span className="text-gradient">Built for Traders. Driven by Knowledge. Focused on Transparency.</span>
            </p>
          </div>
        </Reveal>

        <p className="mt-10 rounded-2xl border border-line bg-surface/60 p-5 text-xs leading-relaxed text-muted" data-testid="about-disclaimer">
          Disclaimer: Funded Traders Club is an independent review and educational platform. We are not a prop
          firm, broker, or financial adviser. Reviews and information are provided for educational and
          informational purposes only. Trading involves risk, and no firm or payout is guaranteed.
        </p>
      </section>
      <Footer />
    </div>
  );
}
